import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, Layers, Activity, Search, Filter, ArrowRight,
    CheckCircle2, Clock, Bot, Sparkles, ExternalLink,
    Plus, ChevronRight, Check, AlertTriangle, ShieldCheck,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, ListOrdered,
    UserCheck, ShieldAlert, History, Lock, Users, Server,
    Loader2
} from "lucide-react";

export default function DetectionRouting() {
    const navigate = useNavigate();
    const [routingData, setRoutingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("universal"); // universal | per-rule | audit | entities
    const [toast, setToast] = useState(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [newRuleForm, setNewRuleForm] = useState({
        name: "",
        description: "",
        outcome: "Investigate",
        outcomeSub: "",
        exceptions: "None",
        impactedRules: 1
    });

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchRouting();
    }, []);

    const fetchRouting = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/detect/routing`, { headers });
            setRoutingData(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load detection routing:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleRule = async (ruleId, currentEnabled) => {
        const nextState = !currentEnabled;
        try {
            const res = await axios.post(`${backendURL}/api/detect/routing/${ruleId}/toggle`, { enabled: nextState }, { headers });
            setRoutingData(prev => ({
                ...prev,
                universalRules: prev.universalRules.map(r =>
                    r.id === ruleId ? { ...r, enabled: nextState, status: res.data.status } : r
                )
            }));
            setToast(`Routing Policy #${ruleId} ${nextState ? "Enabled" : "Set to Draft"}`);
            setTimeout(() => setToast(null), 2500);
        } catch (err) {
            console.error("Failed to toggle routing rule:", err);
        }
    };

    const handleCreateRule = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${backendURL}/api/detect/routing/universal`, newRuleForm, { headers });
            if (res.data.success) {
                setRoutingData(prev => ({
                    ...prev,
                    universalRules: [...(prev?.universalRules || []), res.data.rule]
                }));
                setAddModalOpen(false);
                setNewRuleForm({
                    name: "",
                    description: "",
                    outcome: "Investigate",
                    outcomeSub: "",
                    exceptions: "None",
                    impactedRules: 1
                });
                setToast("New Routing Policy Created & Active!");
                setTimeout(() => setToast(null), 3000);
            }
        } catch (err) {
            console.error("Failed to create rule:", err);
        }
    };

    const universalRules = routingData?.universalRules || [];
    const perRuleOverrides = routingData?.perRuleOverrides || [];
    const auditLogs = routingData?.auditLogs || [];
    const entityLists = routingData?.entityLists || { vipUsers: [], approvedScanners: [], criticalSubnets: [] };

    if (loading && !routingData) {
        return (
            <div className="w-full min-h-[500px] flex flex-col items-center justify-center space-y-4">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                <p className="text-slate-400 text-sm font-mono">Loading real-time triage automation & routing policies...</p>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6 pb-12 animate-in fade-in duration-300">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>05</span>
                <span>/</span>
                <span className="text-slate-400">Detection Routing</span>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (4.5 cols) ═══ */}
                <div className="lg:col-span-4 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            Route every detection to the right outcome.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            Decide what happens to each detection before it ever reaches a person. Investigate everything critical, sample the low-severity noise, suppress known-good sources, or send confirmed threats straight to enrichment and quarantine. Routing runs continuously, with <strong>humans on the loop</strong> and a full audit trail.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Investigate, sample, suppress, or escalate by rule.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Exceptions for VIPs and known-good sources.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Every routing decision logged in real-time audit.</span>
                        </div>
                    </div>

                    {/* Quick Stats Summary Card */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[12px] font-bold text-white uppercase tracking-wider">Triage Automation Status</span>
                            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                Active Pipeline
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-[12px]">
                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-500 font-mono uppercase block">Active Policies</span>
                                <span className="text-[18px] font-bold text-white font-mono">
                                    {universalRules.filter(r => r.enabled).length} of {universalRules.length}
                                </span>
                            </div>
                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-500 font-mono uppercase block">Active Rule Overrides</span>
                                <span className="text-[18px] font-bold text-blue-400 font-mono">
                                    {perRuleOverrides.length}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: Routing Rules Canvas (7.5 cols) ═══ */}
                <div className="lg:col-span-8 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-2xl space-y-6">
                    
                    {/* Top 4 Tabs */}
                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                        <div className="flex items-center gap-6 text-[13px] font-bold overflow-x-auto">
                            <button
                                onClick={() => setActiveTab("universal")}
                                className={`pb-2 transition-all relative whitespace-nowrap ${
                                    activeTab === "universal" ? "text-blue-400" : "text-slate-400 hover:text-slate-200"
                                }`}
                            >
                                Universal Rule Routing
                                {activeTab === "universal" && (
                                    <span className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                                )}
                            </button>

                            <button
                                onClick={() => setActiveTab("per-rule")}
                                className={`pb-2 transition-all relative whitespace-nowrap ${
                                    activeTab === "per-rule" ? "text-blue-400" : "text-slate-400 hover:text-slate-200"
                                }`}
                            >
                                Per-Rule Routing ({perRuleOverrides.length})
                                {activeTab === "per-rule" && (
                                    <span className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                                )}
                            </button>

                            <button
                                onClick={() => setActiveTab("audit")}
                                className={`pb-2 transition-all relative whitespace-nowrap ${
                                    activeTab === "audit" ? "text-blue-400" : "text-slate-400 hover:text-slate-200"
                                }`}
                            >
                                Audit Logs ({auditLogs.length})
                                {activeTab === "audit" && (
                                    <span className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                                )}
                            </button>

                            <button
                                onClick={() => setActiveTab("entities")}
                                className={`pb-2 transition-all relative whitespace-nowrap ${
                                    activeTab === "entities" ? "text-blue-400" : "text-slate-400 hover:text-slate-200"
                                }`}
                            >
                                Entity Lists
                                {activeTab === "entities" && (
                                    <span className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                                )}
                            </button>
                        </div>

                        <button
                            onClick={() => setAddModalOpen(true)}
                            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11.5px] font-bold shadow-md transition-all whitespace-nowrap"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Add Rule
                        </button>
                    </div>

                    {/* ═══ TAB 1: UNIVERSAL RULE ROUTING ═══ */}
                    {activeTab === "universal" && (
                        <div className="space-y-4 animate-in fade-in duration-150">
                            
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead className="border-b border-white/[0.06] bg-black/40 text-[10.5px] uppercase font-mono text-slate-400">
                                        <tr>
                                            <th className="py-3 px-3">Hierarchy</th>
                                            <th className="py-3 px-3">Status</th>
                                            <th className="py-3 px-4">Name</th>
                                            <th className="py-3 px-3">Exceptions</th>
                                            <th className="py-3 px-4">Outcome</th>
                                            <th className="py-3 px-4 text-right">Impacted rules</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/[0.04] text-[12px]">
                                        {universalRules.map((rule) => (
                                            <tr key={rule.id} className="hover:bg-white/[0.02] transition-colors group">
                                                
                                                {/* Hierarchy + Toggle Switch */}
                                                <td className="py-4 px-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            onClick={() => handleToggleRule(rule.id, rule.enabled)}
                                                            className={`w-9 h-5 rounded-full transition-colors relative p-0.5 focus:outline-none ${
                                                                rule.enabled ? "bg-blue-600" : "bg-slate-700"
                                                            }`}
                                                        >
                                                            <div
                                                                className={`w-4 h-4 rounded-full bg-white transition-transform ${
                                                                    rule.enabled ? "translate-x-4" : "translate-x-0"
                                                                }`}
                                                            />
                                                        </button>
                                                        <span className="font-mono font-bold text-slate-400 text-[13px]">
                                                            {rule.hierarchy}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Status Pill */}
                                                <td className="py-4 px-3 whitespace-nowrap">
                                                    <span className={`text-[9.5px] font-mono uppercase px-2 py-0.5 rounded font-bold border ${
                                                        rule.status === "ENABLED"
                                                            ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                                                            : "bg-slate-700/30 text-slate-400 border-white/[0.08]"
                                                    }`}>
                                                        {rule.status}
                                                    </span>
                                                </td>

                                                {/* Name & Description */}
                                                <td className="py-4 px-4 max-w-xs">
                                                    <h4 className="font-bold text-white leading-snug">{rule.name}</h4>
                                                    <p className="text-[11px] text-slate-400 mt-0.5 leading-tight">{rule.description}</p>
                                                </td>

                                                {/* Exceptions */}
                                                <td className="py-4 px-3 whitespace-nowrap">
                                                    {rule.exceptions !== "None" ? (
                                                        <span className="text-[10px] font-mono bg-amber-500/15 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded font-semibold">
                                                            {rule.exceptions}
                                                        </span>
                                                    ) : (
                                                        <span className="text-[11px] text-slate-500 font-mono">None</span>
                                                    )}
                                                </td>

                                                {/* Outcome */}
                                                <td className="py-4 px-4 whitespace-nowrap">
                                                    <span className="font-bold text-white block">{rule.outcome}</span>
                                                    {rule.outcomeSub && (
                                                        <span className="text-[10.5px] text-slate-400 font-mono block">
                                                            {rule.outcomeSub}
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Impacted Rules */}
                                                <td className="py-4 px-4 text-right font-mono font-bold text-slate-200 text-[13px]">
                                                    {rule.impactedRules}
                                                </td>

                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                        </div>
                    )}

                    {/* ═══ TAB 2: PER-RULE ROUTING OVERRIDES ═══ */}
                    {activeTab === "per-rule" && (
                        <div className="space-y-4 animate-in fade-in duration-150 text-[12px]">
                            <div className="p-4 bg-black/30 border border-white/[0.06] rounded-xl flex items-center justify-between">
                                <div>
                                    <h4 className="font-bold text-white">Specific Rule Overrides ({perRuleOverrides.length})</h4>
                                    <p className="text-[11px] text-slate-400 mt-0.5">Active triage automation policies configured per connected detection rule.</p>
                                </div>
                                <Link
                                    to="/detection-rules"
                                    className="text-[11.5px] font-bold text-blue-400 hover:text-blue-300 transition-colors"
                                >
                                    Browse Detection Rules Inventory →
                                </Link>
                            </div>

                            <div className="space-y-2.5">
                                {perRuleOverrides.length === 0 ? (
                                    <div className="p-8 text-center text-slate-500 italic">No per-rule overrides configured.</div>
                                ) : (
                                    perRuleOverrides.map((item) => (
                                        <div key={item.id} className="p-3.5 bg-black/20 border border-white/[0.04] rounded-xl flex items-center justify-between hover:border-white/[0.1] transition-all">
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className="w-7 h-7 rounded-md flex items-center justify-center font-mono font-bold text-[10px] text-white shadow-sm flex-shrink-0"
                                                    style={{ backgroundColor: item.color || "#4F46E5" }}
                                                >
                                                    {item.badge || item.source?.substring(0, 2).toUpperCase() || "QT"}
                                                </div>
                                                <div>
                                                    <span className="font-bold text-white block">{item.name}</span>
                                                    <span className="text-[11px] text-blue-400 font-mono">{item.override}</span>
                                                </div>
                                            </div>
                                            <span className="text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded">
                                                {item.status || "Active"}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* ═══ TAB 3: AUDIT LOGS ═══ */}
                    {activeTab === "audit" && (
                        <div className="space-y-3 animate-in fade-in duration-150 text-[12px]">
                            <div className="border border-white/[0.06] rounded-xl overflow-hidden bg-black/20">
                                <div className="p-3 border-b border-white/[0.06] bg-black/40 font-bold text-white flex items-center justify-between">
                                    <span>Recent Triage Decisions Log ({auditLogs.length})</span>
                                    <span className="text-[10px] font-mono text-slate-400">Live MongoDB Incidents Trail</span>
                                </div>
                                <div className="divide-y divide-white/[0.04]">
                                    {auditLogs.length === 0 ? (
                                        <div className="p-8 text-center text-slate-500 italic">No triage audit records yet.</div>
                                    ) : (
                                        auditLogs.map((log) => (
                                            <div key={log.id} className="p-3.5 hover:bg-white/[0.02] flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                <div className="space-y-0.5">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10.5px] font-mono bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded font-bold">
                                                            {log.alertId}
                                                        </span>
                                                        <span className="font-semibold text-white">{log.alertTitle}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-[11px] text-slate-400 font-mono">
                                                        <span>Rule: {log.ruleApplied}</span>
                                                        <span>•</span>
                                                        <span className="text-emerald-400">{log.action}</span>
                                                    </div>
                                                </div>

                                                <div className="text-right flex-shrink-0">
                                                    <span className="text-[10px] font-mono text-slate-500 block">
                                                        {new Date(log.timestamp).toLocaleTimeString()}
                                                    </span>
                                                    <span className="text-[10px] font-mono uppercase text-slate-400">
                                                        Outcome: {log.outcome}
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══ TAB 4: ENTITY LISTS ═══ */}
                    {activeTab === "entities" && (
                        <div className="space-y-4 animate-in fade-in duration-150 text-[12px]">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                
                                {/* VIP Users */}
                                <div className="p-4 bg-black/30 border border-white/[0.06] rounded-xl space-y-3">
                                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-2">
                                        <span className="font-bold text-white flex items-center gap-1.5">
                                            <Users className="w-3.5 h-3.5 text-amber-400" />
                                            VIP Users
                                        </span>
                                        <span className="text-[10px] font-mono text-slate-500">{entityLists.vipUsers?.length || 0} entries</span>
                                    </div>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto custom-scrollbar">
                                        {entityLists.vipUsers?.map((user, idx) => (
                                            <div key={idx} className="p-1.5 bg-black/40 rounded border border-white/[0.04] text-[11px] font-mono text-slate-200">
                                                {user}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Approved Scanners / Gateways */}
                                <div className="p-4 bg-black/30 border border-white/[0.06] rounded-xl space-y-3">
                                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-2">
                                        <span className="font-bold text-white flex items-center gap-1.5">
                                            <Server className="w-3.5 h-3.5 text-emerald-400" />
                                            Active Gateway Sensors
                                        </span>
                                        <span className="text-[10px] font-mono text-slate-500">{entityLists.approvedScanners?.length || 0} active</span>
                                    </div>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto custom-scrollbar">
                                        {entityLists.approvedScanners?.map((ip, idx) => (
                                            <div key={idx} className="p-1.5 bg-black/40 rounded border border-white/[0.04] text-[11px] font-mono text-slate-200">
                                                {ip}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Critical Server Subnets */}
                                <div className="p-4 bg-black/30 border border-white/[0.06] rounded-xl space-y-3">
                                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-2">
                                        <span className="font-bold text-white flex items-center gap-1.5">
                                            <Globe className="w-3.5 h-3.5 text-blue-400" />
                                            Critical Subnets
                                        </span>
                                        <span className="text-[10px] font-mono text-slate-500">{entityLists.criticalSubnets?.length || 0} ranges</span>
                                    </div>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto custom-scrollbar">
                                        {entityLists.criticalSubnets?.map((sub, idx) => (
                                            <div key={idx} className="p-1.5 bg-black/40 rounded border border-white/[0.04] text-[11px] font-mono text-slate-200">
                                                {sub}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}

                </div>

            </div>

            {/* ─── Add Routing Rule Modal ─── */}
            {addModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[15px] font-bold text-white">Add Universal Routing Policy</h3>
                            <button onClick={() => setAddModalOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateRule} className="space-y-3.5 text-[12px]">
                            <div>
                                <label className="text-slate-400 block mb-1">Rule Policy Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Lateral Movement High Risk Always Escalate"
                                    value={newRuleForm.name}
                                    onChange={(e) => setNewRuleForm({ ...newRuleForm, name: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Description</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Any alert involving SMB/RDP lateral movement triggers quarantine"
                                    value={newRuleForm.description}
                                    onChange={(e) => setNewRuleForm({ ...newRuleForm, description: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-slate-400 block mb-1">Triage Outcome</label>
                                    <select
                                        value={newRuleForm.outcome}
                                        onChange={(e) => setNewRuleForm({ ...newRuleForm, outcome: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        <option value="Investigate">Investigate</option>
                                        <option value="Do not investigate">Do not investigate (Suppress)</option>
                                        <option value="Quarantine & notify">Quarantine & notify</option>
                                        <option value="Enrich & tag">Enrich & tag</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-slate-400 block mb-1">Exceptions List</label>
                                    <select
                                        value={newRuleForm.exceptions}
                                        onChange={(e) => setNewRuleForm({ ...newRuleForm, exceptions: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        <option value="None">None</option>
                                        <option value="entity on VIP list">entity on VIP list</option>
                                        <option value="approved scanner IP">approved scanner IP</option>
                                        <option value="critical server subnet">critical server subnet</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Impacted Detection Rules (Est.)</label>
                                <input
                                    type="number"
                                    value={newRuleForm.impactedRules}
                                    onChange={(e) => setNewRuleForm({ ...newRuleForm, impactedRules: parseInt(e.target.value) || 1 })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none font-mono"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => setAddModalOpen(false)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md"
                                >
                                    Save Policy
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
